<template>
  <div class="qrcode-container">
    <!-- 渐变动画页头 -->
    <div class="gradient-header">
      <div class="checkerboard-overlay"></div>
      <div class="header-content">
        <div class="header-icon">
          <i class="el-icon-mobile-phone"></i>
        </div>
        <span>缴费二维码生成</span>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <el-card class="content-card" shadow="never">
      <el-row :gutter="40">
        <!-- 左侧：输入区域 -->
        <el-col :span="12">
          <div class="input-section">
            <h3 class="section-title">
              <i class="el-icon-edit"></i>
              输入缴费号码
            </h3>

            <el-form
              ref="qrcodeForm"
              :model="formData"
              :rules="rules"
              label-width="100px"
              class="qrcode-form"
            >
              <el-form-item label="缴费号码" prop="iccid">
                <el-input
                  v-model="formData.iccid"
                  placeholder="请输入缴费号码（聚水潭唯一码）"
                  clearable
                  @keyup.enter.native="handleGenerate"
                >
                  <template slot="prepend">
                    <i class="el-icon-postcard"></i>
                  </template>
                </el-input>
              </el-form-item>

              <el-form-item>
                <el-button
                  type="primary"
                  icon="el-icon-picture"
                  :loading="generateLoading"
                  @click="handleGenerate"
                  size="large"
                  style="width: 100%"
                >
                  {{ generateLoading ? '验证中...' : '生成二维码' }}
                </el-button>
              </el-form-item>

              <el-alert
                title="使用说明"
                type="info"
                :closable="false"
                style="margin-top: 20px"
              >
                <div style="font-size: 13px; line-height: 1.8">
                  1. 输入缴费号码（聚水潭唯一码）<br>
                  2. 点击"生成二维码"按钮<br>
                  3. 验证成功后将生成充值二维码<br>
                  4. 用户扫码后可直接选择套餐充值
                </div>
              </el-alert>
            </el-form>
          </div>
        </el-col>

        <!-- 右侧：二维码展示区域 -->
        <el-col :span="12">
          <div class="qrcode-section">
            <h3 class="section-title">
              <i class="el-icon-picture-outline"></i>
              充值二维码
            </h3>

            <!-- 二维码显示区域 -->
            <div class="qrcode-display" v-loading="generateLoading">
              <div v-if="!qrcodeUrl && !generateLoading" class="empty-state">
                <i class="el-icon-picture-outline"></i>
                <p>请输入缴费号码并生成二维码</p>
              </div>

              <div v-if="qrcodeUrl" class="qrcode-wrapper">
                <div class="qrcode-image" ref="qrcodeContainer">
                  <!-- 二维码将在这里生成 -->
                </div>

                <div class="qrcode-info">
                  <el-tag type="success" size="medium">
                    <i class="el-icon-success"></i>
                    验证成功
                  </el-tag>
                  <p class="iccid-text">{{ formData.iccid }}</p>
                  <p class="url-text">充值链接: {{ qrcodeUrl }}</p>
                </div>

                <div class="qrcode-actions">
                  <el-button
                    type="primary"
                    icon="el-icon-download"
                    @click="downloadQRCode"
                    plain
                  >
                    下载二维码
                  </el-button>
                  <el-button
                    icon="el-icon-document-copy"
                    @click="copyLink"
                  >
                    复制链接
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import { verifyICCID } from '@/api/qrpay'
import QRCode from 'qrcodejs2'

export default {
  name: 'IccidQRCode',
  data() {
    // 验证缴费号码
    const validatePaymentCode = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入缴费号码'))
      } else if (value.trim().length === 0) {
        callback(new Error('缴费号码不能为空'))
      } else {
        callback()
      }
    }

    return {
      formData: {
        iccid: ''
      },
      rules: {
        iccid: [
          { required: true, validator: validatePaymentCode, trigger: 'blur' }
        ]
      },
      generateLoading: false,
      qrcodeUrl: '',
      qrcodeInstance: null
    }
  },
  methods: {
    // 生成二维码
    async handleGenerate() {
      this.$refs.qrcodeForm.validate(async valid => {
        if (!valid) return

        this.generateLoading = true
        try {
          // 1. 调用验证接口
          const response = await verifyICCID(this.formData.iccid)

          console.log('验证响应:', response)

          if (response.status === 1) {
            // 2. 验证成功，生成充值链接
            // 格式: https://pay1.whjhft.com?key={iccid}&tc=1
            this.qrcodeUrl = `https://pay1.whjhft.com?key=${this.formData.iccid}&tc=1`

            // 3. 等待DOM更新后生成二维码
            this.$nextTick(() => {
              this.generateQRCode()
            })

            this.$message.success('验证成功，二维码已生成')
          } else {
            this.$message.error(response.message || '验证失败')
          }
        } catch (error) {
          console.error('验证失败:', error)
          this.$message.error(error.message || '验证失败，请稍后重试')
        } finally {
          this.generateLoading = false
        }
      })
    },

    // 生成二维码图片
    generateQRCode() {
      // 清除旧的二维码
      if (this.qrcodeInstance) {
        this.$refs.qrcodeContainer.innerHTML = ''
      }

      // 生成新二维码
      this.qrcodeInstance = new QRCode(this.$refs.qrcodeContainer, {
        text: this.qrcodeUrl,
        width: 280,
        height: 280,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H
      })
    },

    // 下载二维码
    downloadQRCode() {
      try {
        const canvas = this.$refs.qrcodeContainer.querySelector('canvas')
        if (!canvas) {
          this.$message.error('二维码生成失败')
          return
        }

        // 转换为图片并下载
        const url = canvas.toDataURL('image/png')
        const link = document.createElement('a')
        link.href = url
        link.download = `缴费码_${this.formData.iccid}_充值二维码.png`
        link.click()

        this.$message.success('二维码已下载')
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败')
      }
    },

    // 复制链接
    copyLink() {
      try {
        const input = document.createElement('input')
        input.value = this.qrcodeUrl
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)

        this.$message.success('链接已复制到剪贴板')
      } catch (error) {
        console.error('复制失败:', error)
        this.$message.error('复制失败')
      }
    }
  },
  beforeDestroy() {
    // 清理二维码实例
    if (this.qrcodeInstance) {
      this.qrcodeInstance = null
    }
  }
}
</script>

<style lang="scss" scoped>
.qrcode-container {
  padding: 20px;
}

/* 渐变动画页头样式 */
.gradient-header {
  height: 80px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 24px;
  color: white;
  font-size: 18px;
  border-radius: 4px;
  margin-bottom: 20px;
  overflow: hidden;
}

.checkerboard-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255,255,255,0.1) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.1) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.1) 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}

.header-icon {
  margin-right: 12px;

  i {
    font-size: 24px;
  }
}

.content-card {
  margin-top: 20px;
  min-height: 600px;
}

.section-title {
  font-size: 16px;
  color: #303133;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #E4E7ED;

  i {
    margin-right: 8px;
    color: #667eea;
  }
}

.input-section {
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
  min-height: 500px;
}

.qrcode-form {
  margin-top: 30px;
}

.qrcode-section {
  padding: 20px;
  background: #fafbfc;
  border-radius: 8px;
  min-height: 500px;
}

.qrcode-display {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 450px;
}

.empty-state {
  text-align: center;
  color: #909399;

  i {
    font-size: 80px;
    margin-bottom: 20px;
    opacity: 0.3;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
}

.qrcode-wrapper {
  text-align: center;
  width: 100%;
}

.qrcode-image {
  display: inline-block;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.qrcode-info {
  margin: 20px 0;

  .iccid-text {
    font-size: 16px;
    font-weight: bold;
    color: #303133;
    margin: 10px 0;
  }

  .url-text {
    font-size: 12px;
    color: #909399;
    word-break: break-all;
    margin: 5px 0;
  }
}

.qrcode-actions {
  margin-top: 20px;

  .el-button {
    margin: 0 5px;
  }
}
</style>
